/**
 * Lead Advisor approval matrix: mirrors backend org-setup delegate docx gate
 * (see backend/src/workspaceTasks.ts getOrgSetupDelegateArtefactState).
 */

import { artefactFileNameUiDisplay } from "./artefactDisplayFileName";
import { parseReportsToRef } from "./orgChartModel";

/** @param {object | null} row */
export function companyArtefactTemplateRowIsDocx(row, brandedUrl) {
  const r = row && typeof row === "object" && !Array.isArray(row) ? row : null;
  const label = String(r?.fileName ?? r?.templateId ?? brandedUrl ?? "").toLowerCase();
  if (label.endsWith(".docx")) return true;
  const u = String(brandedUrl ?? "").split("?")[0].toLowerCase();
  return u.endsWith(".docx");
}

/** @param {object} wiz */
export function orgArtefactRowHasDelegateAssignment(wiz, rowId) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const del = w.companyArtefactAssigneeDelegateMemberIdByRowId;
  if (del && typeof del === "object" && !Array.isArray(del)) {
    const v = del[rowId];
    if (v != null && String(v).trim()) return true;
  }
  const org = w.companyArtefactAssigneeByRowId;
  if (org && typeof org === "object" && !Array.isArray(org)) {
    const v = org[rowId];
    if (v != null && String(v).trim()) return true;
  }
  return false;
}

export function normalizePersonName(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/**
 * @param {object} wiz
 * @param {unknown[]} artefactIndexRows
 * @returns {{ released: boolean, allottedAt: string, pendingDelegateDocx: boolean, completedAt: string | null }}
 */
export function getOrgSetupDelegateArtefactStateFromWiz(wiz, artefactIndexRows) {
  const empty = { released: false, allottedAt: "", pendingDelegateDocx: false, completedAt: null };
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const rel = w.companyArtefactsReleasedToDelegatesAt;
  const released = typeof rel === "string" && rel.trim().length > 0;
  const allottedAt = released ? rel : "";
  if (!released) return empty;

  const rows = Array.isArray(artefactIndexRows) ? artefactIndexRows : [];
  const branded = w.companyArtefactBrandedBlobUrlByRowId;
  const submittedMap = w.companyArtefactDelegateSubmittedAtByRowId;

  let pendingDelegateDocx = false;
  const submittedTimes = [];

  for (const rawRow of rows) {
    if (!rawRow || typeof rawRow !== "object" || Array.isArray(rawRow)) continue;
    const rowObj = rawRow;
    const rowId = rowObj.id != null ? String(rowObj.id) : "";
    if (!rowId) continue;

    const brandedUrl =
      branded && typeof branded === "object" && !Array.isArray(branded) ? branded[rowId] : undefined;
    if (!brandedUrl || !String(brandedUrl).trim()) continue;
    if (!orgArtefactRowHasDelegateAssignment(w, rowId)) continue;
    if (!companyArtefactTemplateRowIsDocx(rawRow, brandedUrl)) continue;

    const subAt =
      submittedMap && typeof submittedMap === "object" && !Array.isArray(submittedMap)
        ? submittedMap[rowId]
        : undefined;
    if (subAt != null && String(subAt).trim()) {
      submittedTimes.push(String(subAt));
    } else {
      pendingDelegateDocx = true;
    }
  }

  let completedAt = null;
  if (!pendingDelegateDocx) {
    if (submittedTimes.length > 0) {
      completedAt = submittedTimes.reduce((a, b) => (a > b ? a : b));
    } else {
      completedAt = allottedAt;
    }
  }

  return { released, allottedAt, pendingDelegateDocx, completedAt };
}

/**
 * Branded + assigned + docx + submitted row ids (matrix rows).
 * @param {object} wiz
 * @param {unknown[]} artefactIndexRows
 * @returns {string[]}
 */
export function listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const rows = Array.isArray(artefactIndexRows) ? artefactIndexRows : [];
  const branded = w.companyArtefactBrandedBlobUrlByRowId;
  const submittedMap = w.companyArtefactDelegateSubmittedAtByRowId;
  const out = [];

  for (const rawRow of rows) {
    if (!rawRow || typeof rawRow !== "object" || Array.isArray(rawRow)) continue;
    const rowObj = rawRow;
    const rowId = rowObj.id != null ? String(rowObj.id) : "";
    if (!rowId) continue;

    const brandedUrl =
      branded && typeof branded === "object" && !Array.isArray(branded) ? branded[rowId] : undefined;
    if (!brandedUrl || !String(brandedUrl).trim()) continue;
    if (!orgArtefactRowHasDelegateAssignment(w, rowId)) continue;
    if (!companyArtefactTemplateRowIsDocx(rawRow, brandedUrl)) continue;

    const subAt =
      submittedMap && typeof submittedMap === "object" && !Array.isArray(submittedMap)
        ? submittedMap[rowId]
        : undefined;
    if (subAt != null && String(subAt).trim()) out.push(rowId);
  }
  return out;
}

export function artefactBasename(row) {
  const raw = row?.fileName || row?.templateId || "";
  if (!raw) return "";
  const parts = String(raw).split(/[/\\]/);
  return parts[parts.length - 1] || raw;
}

/**
 * Ordered list of Reports to cell values (personnel table order), excluding empty placeholders.
 * @param {unknown[]} normalizedOrgRows
 * @returns {string[]}
 */
export function buildReportsToCycle(normalizedOrgRows) {
  const list = Array.isArray(normalizedOrgRows) ? normalizedOrgRows : [];
  const out = [];
  for (const r of list) {
    const raw = r?.reportsToRaw;
    if (parseReportsToRef(raw) == null) continue;
    const cell = String(raw ?? "")
      .trim()
      .replace(/\s+/g, " ");
    if (cell) out.push(cell);
  }
  return out;
}

export function findProjectMemberByOrgName(members, approverName) {
  if (!approverName) return null;
  const want = normalizePersonName(approverName);
  return (Array.isArray(members) ? members : []).find((m) => normalizePersonName(m.name) === want) || null;
}

export function memberRoleToNormalizedToken(role) {
  const r = String(role || "")
    .toLowerCase()
    .replace(/-/g, "_");
  const map = {
    lead_advisor: "LEAD_ADVISOR",
    advisor: "ADVISOR",
    company_spoc: "COMPANY_SPOC",
    company_delegate: "COMPANY_DELEGATE",
    admin: "ADVISOR",
    platform_admin: "LEAD_ADVISOR",
  };
  return map[r] || "UNKNOWN";
}

/**
 * @param {object} prevMatrix
 * @param {string[]} rowIds
 */
export function mergeApprovalMatrixEntries(prevMatrix, rowIds) {
  const prev =
    prevMatrix && typeof prevMatrix === "object" && !Array.isArray(prevMatrix) ? { ...prevMatrix } : {};
  for (const id of rowIds) {
    const key = String(id);
    if (!prev[key]) {
      prev[key] = { escalationMemberId: null, slaKey: null };
    } else {
      const e = prev[key];
      prev[key] = {
        escalationMemberId: e.escalationMemberId != null && String(e.escalationMemberId).trim()
          ? String(e.escalationMemberId)
          : null,
        slaKey: e.slaKey != null && String(e.slaKey).trim() ? String(e.slaKey) : null,
      };
    }
  }
  return prev;
}

export const APPROVAL_MATRIX_SLA_OPTIONS = [
  { value: "bd_3", label: "3 business days" },
  { value: "bd_5", label: "5 business days" },
  { value: "bd_7", label: "7 business days" },
  { value: "bd_10", label: "10 business days" },
];

/**
 * True when each row id has a non-empty escalation member and SLA in the persisted matrix map.
 * @param {object} matrixMap — `wiz.leadAdvisorApprovalMatrixByRowId`
 * @param {string[]} rowIds — matrix row ids (same order as the UI table)
 */
export function isApprovalMatrixAssignmentsComplete(matrixMap, rowIds) {
  const ids = Array.isArray(rowIds) ? rowIds.map((id) => String(id)) : [];
  if (ids.length === 0) return false;
  const m = matrixMap && typeof matrixMap === "object" && !Array.isArray(matrixMap) ? matrixMap : {};
  for (const id of ids) {
    const e = m[id];
    if (!e || typeof e !== "object") return false;
    const esc = e.escalationMemberId != null && String(e.escalationMemberId).trim();
    const sla = e.slaKey != null && String(e.slaKey).trim();
    if (!esc || !sla) return false;
  }
  return true;
}

/**
 * @param {object} wiz
 * @param {unknown[]} artefactIndexRows
 * @param {boolean} orgChartApproved
 */
export function canGenerateLeadAdvisorApprovalMatrix(wiz, artefactIndexRows, orgChartApproved) {
  if (!orgChartApproved) return { ok: false, reason: "org" };
  const st = getOrgSetupDelegateArtefactStateFromWiz(wiz, artefactIndexRows);
  if (!st.released) return { ok: false, reason: "not_released" };
  if (st.pendingDelegateDocx) return { ok: false, reason: "pending" };
  const rowIds = listSubmittedDocxBrandedAssignedRowIds(wiz, artefactIndexRows);
  if (rowIds.length === 0) return { ok: false, reason: "no_rows" };
  return { ok: true, state: st, rowIds };
}

/**
 * @param {string[]} rowIds
 * @param {unknown[]} artefactIndexRows
 * @param {unknown[]} normalizedOrgRows
 * @param {unknown[]} members
 */
export function buildApprovalMatrixDisplayRows(rowIds, artefactIndexRows, normalizedOrgRows, members) {
  const byId = new Map((Array.isArray(artefactIndexRows) ? artefactIndexRows : []).map((r) => [String(r.id), r]));
  const cycle = buildReportsToCycle(normalizedOrgRows);
  const n = cycle.length;
  return rowIds.map((rowId, idx) => {
    const id = String(rowId);
    const artefact = byId.get(id);
    const rawReportsTo = n > 0 ? cycle[idx % n] : null;
    const parsed = rawReportsTo ? parseReportsToRef(rawReportsTo) : null;
    const approverName = parsed?.name || "";
    const approverDesignation = parsed?.designation || "";
    const approverMember = approverName ? findProjectMemberByOrgName(members, approverName) : null;
    const approverRoleToken = approverMember ? memberRoleToNormalizedToken(approverMember.role) : null;
    return {
      templateRowId: id,
      fileLabel: artefactFileNameUiDisplay(artefact),
      title: artefact?.name || "",
      approverName,
      approverDesignation,
      approverRoleToken,
    };
  });
}
